import type { WebGLRenderer } from '@seddi/three';
import { SceneGeometryModule } from './domain/scene/sceneGeometryModule';
import { ResourcesContext } from './resources/resourcesContext';
import {
  createRenderer,
  createRendererContextAttributes,
  setRendererSize,
} from './three/renderers/webGlRenderer';
import { convertToneMapping } from './three/toneMapping';
import type { MultiViewElement } from './multiViewElement';
import * as multiViewElement from './multiViewElement';
import { type MultiViewRendererConfig, create as createConfig } from './multiViewRendererConfig';
import type { SceneViewerConfig } from './sceneViewerConfig';
import * as sceneViewerContext from './sceneViewerContext';

export type MultiViewRendererContext = {
  canvas: HTMLCanvasElement;
  config: MultiViewRendererConfig;
  needsUpdate: boolean;
  renderer: WebGLRenderer;
  resources: ResourcesContext;
  readonly views: MultiViewElement[];
};

export const create = (config: Partial<MultiViewRendererConfig>) => {
  const config_ = createConfig(config);

  return { config: config_, needsUpdate: false, views: [] } as MultiViewRendererContext;
};

export const init = (self: MultiViewRendererContext, canvas: HTMLCanvasElement) => {
  const { config } = self;

  self.canvas = canvas;

  // Init renderer.
  const contextAttributes = createRendererContextAttributes();
  contextAttributes.alpha = true;
  const renderer = createRenderer(self.canvas, contextAttributes);
  renderer.toneMapping = convertToneMapping(config.toneMapping);
  renderer.toneMappingExposure = config.toneMappingExposure;
  renderer.setClearColor('#000000', 0);

  // TODO: Resource context config.
  const resources = new ResourcesContext(renderer);
  self.renderer = renderer;
  self.resources = resources;
  self.needsUpdate = true;
};

export const dispose = (self: MultiViewRendererContext) => {
  // TODO: Dispose pipeline
  // free GPU resources
  self.resources && self.resources.dispose();
  self.resources = undefined;
  self.renderer && self.renderer.dispose();
  self.renderer = undefined;
  // TODO: each view scene reconciler should dispose its own pieces
  const geometryCache = SceneGeometryModule.instance.geometryCache;
  geometryCache.dispose();
  // free DOM references
  self.canvas = undefined;
  self.views.length = 0;
};

/**
 * Resizes the internal buffers.
 * @param width - Width in content box size coords.
 * @param height - Height in content box size coords.
 */
export const setSize = (self: MultiViewRendererContext, width: number, height: number) => {
  const devicePixelRatio = window.devicePixelRatio;
  // Update default framebuffer
  setRendererSize(self.renderer, width, height, devicePixelRatio);
  // Mark dirty
  self.needsUpdate = true;
};

export const draw = (self: MultiViewRendererContext) => {
  const { renderer, views } = self;

  renderer.clear();
  renderer.setScissorTest(true);

  views.forEach((view) => {
    const rect = multiViewElement.getBoundingClientRect(view);

    // check if it's offscreen. If so skip it
    if (
      rect.bottom < 0 ||
      rect.top > renderer.domElement.clientHeight ||
      rect.right < 0 ||
      rect.left > renderer.domElement.clientWidth
    ) {
      return; // it's off screen
    }

    // Configure scissor and viewport
    const width = rect.right - rect.left;
    const height = rect.bottom - rect.top;
    const left = rect.left;
    const bottom = renderer.domElement.clientHeight - rect.bottom;

    renderer.setViewport(left, bottom, width, height);
    renderer.setScissor(left, bottom, width, height);

    // Render view
    sceneViewerContext.render(view);
  });

  renderer.setScissorTest(false);

  for (const view of views) {
    view.needsUpdate = false;
  }
  self.needsUpdate = false;

  onAfterDraw(self);
};

export const createView = (
  self: MultiViewRendererContext,
  config: Partial<SceneViewerConfig>,
  domElement: HTMLElement,
): MultiViewElement => {
  const { renderer, resources, views } = self;
  const view = multiViewElement.create(renderer, resources, domElement, config);
  views.push(view);
  self.needsUpdate = true;
  return view;
};

export const removeView = (self: MultiViewRendererContext, id: string) => {
  const { views } = self;
  const index = views.findIndex((v) => v.id === id);
  views.splice(index, 1);
  self.needsUpdate = true;
};

const onAfterDraw = (self: MultiViewRendererContext) => {
  self.resources.commit();
};
